import React from "react";
import styled, { css } from "styled-components";
import { colors } from "src/theme/colors";
import { line, radii } from "src/theme/ui";
import { useIsRequired } from "src/components/Form/requiredFields";
import {
  FieldSize,
  fieldChrome,
  fieldEdge,
  fieldFocus,
  fieldHeight,
  fieldHover,
  fieldInvalid,
} from "./field";

/**
 * Input — football's form field, ported.
 *
 * A label above, the field, and at most one line of help below it. The help
 * line is either an error or a hint, never both: an error replaces the hint,
 * because a reader who has just been told what is wrong does not also need to
 * be told what was expected.
 */

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

/**
 * The label. Small and firm rather than large and grey, so it reads as a
 * caption on the control instead of competing with the value inside it.
 */
export const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  font-weight: 600;
  color: ${colors.gray[800]};
`;

/* The mark carries its meaning by shape, so it survives a colour-blind reader
   and needs no hover; the word carries it to a screen reader. */
const RequiredMark = styled.span`
  margin-left: 2px;
  color: ${colors.gray[600]};
`;

const ScreenReaderOnly = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;

const Control = styled.input<{ $size: FieldSize; $invalid: boolean }>`
  ${fieldChrome}
  width: 100%;
  height: ${({ $size }) => fieldHeight[$size]}px;
  padding: 0 12px;
  border-color: ${({ $size }) => fieldEdge[$size]};
  font-size: 13px;
  font-family: inherit;

  ${fieldHover}
  ${fieldFocus}
  ${({ $invalid }) => $invalid && fieldInvalid}

  &::placeholder {
    color: ${colors.gray[400]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const Help = styled.p<{ $error: boolean }>`
  margin: 0;
  font-size: 11.5px;
  line-height: 1.45;
  color: ${({ $error }) => ($error ? colors.error[600] : colors.gray[600])};
`;

/**
 * The in-card search: 32px, filled rather than white, on a control edge at an
 * 8px corner. Quieter than a form field because it filters what is already on
 * screen rather than collecting a value.
 */
export const SearchInput = styled.input`
  ${fieldChrome}
  height: 32px;
  padding: 0 12px;
  border-color: ${line.field};
  border-radius: ${radii.control}px;
  background: ${colors.gray[50]};
  font-size: 13px;
  font-family: inherit;

  ${fieldHover}
  ${fieldFocus}

  &::placeholder {
    color: ${colors.gray[400]};
  }
`;

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  label?: string;
  /** Shown in place of the hint. Also reddens the edge. */
  error?: string;
  hint?: string;
  /** `field` (default) is the 38px form field; `control` the 34px toolbar one. */
  size?: FieldSize;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, size = "field", id, className, ...props }, ref) => {
    const inputId = id ?? label?.toLowerCase().replace(/\s+/g, "-");
    /* The prop still wins; without one the surrounding form's schema
       decides, so a required field cannot go unmarked. */
    const isRequired = props.required ?? useIsRequired(props.name);

    return (
      <Wrap className={className}>
        {label && (
          <Label htmlFor={inputId}>
            <span>
              {label}
              {isRequired && (
                <>
                  <RequiredMark aria-hidden="true">*</RequiredMark>
                  <ScreenReaderOnly> required</ScreenReaderOnly>
                </>
              )}
            </span>
          </Label>
        )}
        <Control
          ref={ref}
          id={inputId}
          $size={size}
          $invalid={!!error}
          aria-invalid={error ? true : undefined}
          {...props}
        />
        {(error || hint) && (
          <Help $error={!!error}>{error || hint}</Help>
        )}
      </Wrap>
    );
  },
);

Input.displayName = "Input";

export default Input;
